import { useState } from "react";
import { motion } from "framer-motion";
import { Lock, Loader2, ShieldAlert } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { adminAuth, setAdminToken } from "@/lib/api";

export const PinGate = ({ onSuccess }) => {
  const [pin, setPin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!pin.trim()) {
      setError("Zadej PIN.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const data = await adminAuth(pin.trim());
      setAdminToken(data.token);
      onSuccess();
    } catch (err) {
      setError(err?.response?.data?.detail || "Nesprávný PIN. Zkus to znovu.");
      setPin("");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid="admin-pin-gate"
      className="carbon-bg relative flex min-h-screen items-center justify-center overflow-hidden px-4"
    >
      <div className="speed-lines absolute inset-0" />
      <div className="hero-glow absolute inset-0" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="clip-corner relative z-10 w-full max-w-sm border border-white/10 bg-[#0F1217]/90 p-8 backdrop-blur-md"
      >
        <div className="clip-corner-sm mx-auto flex h-14 w-14 items-center justify-center bg-[#B7FF2A]/10 text-[#B7FF2A]">
          <Lock className="h-7 w-7" />
        </div>
        <div className="mt-5 text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#B7FF2A]">
            // Vstup jen pro tým
          </div>
          <h1
            data-testid="admin-pin-headline"
            className="mt-2 font-display text-4xl uppercase tracking-[0.06em] text-white"
          >
            Zadej PIN
          </h1>
          <p className="mt-2 text-sm text-white/45">
            Admin sekce je chráněná. Zadej čtyřmístný PIN.
          </p>
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="admin-pin-form">
          <Input
            data-testid="admin-pin-input"
            type="password"
            inputMode="numeric"
            autoComplete="off"
            maxLength={8}
            placeholder="••••"
            value={pin}
            onChange={(e) => {
              setPin(e.target.value);
              if (error) setError("");
            }}
            autoFocus
            className="h-14 border-white/15 bg-[#0B0D10] text-center font-mono text-2xl tracking-[0.6em] placeholder:text-white/20"
          />
          {error && (
            <motion.div
              data-testid="admin-pin-error"
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center justify-center gap-2 font-mono text-xs uppercase tracking-[0.12em] text-[#FF3B3B]"
            >
              <ShieldAlert className="h-4 w-4" /> {error}
            </motion.div>
          )}
          <Button
            type="submit"
            disabled={submitting}
            data-testid="admin-pin-submit-button"
            className="light-sweep clip-corner-sm h-12 w-full bg-[#B7FF2A] font-display text-base uppercase tracking-[0.14em] text-[#07080A] hover:bg-[#B7FF2A] hover:brightness-110 disabled:opacity-60"
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Ověřuji…
              </span>
            ) : (
              "Odemknout"
            )}
          </Button>
        </form>

        <div className="mt-6 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-white/25">
          FILIP VIMPEL PIT WALL — #606
        </div>
      </motion.div>
    </section>
  );
};
